import { useState } from "react";
import { toast } from "sonner";
import { Check, X, MessageSquare, Clock, Copy, ShieldCheck, AlertCircle, Calendar } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Customer authorisation summary — surfaces what the customer decided on
 * each line item once they've responded to the quote, so the workshop can
 * mirror those decisions into the DMS.
 *
 * Buckets items into Authorised / Declined / Wants advice / Pending and
 * provides a one-tap "Copy DMS-ready summary" so the advisor doesn't have
 * to retype anything. The advisor can mark the job as "Updated in DMS"
 * once they've actioned it — that flag is the audit trail showing the
 * customer's wishes were carried through to the workshop's system of
 * record.
 */
export default function CustomerResponseSummary({ job, onUpdated }) {
  const items = job.items || [];
  const approved = items.filter((i) => i.status === "approved");
  const declined = items.filter((i) => i.status === "rejected");
  const needsAdvice = items.filter((i) => i.status === "needs_advice");
  const rebook = items.filter((i) => i.status === "rebook");
  const pending = items.filter((i) => !i.status || i.status === "pending");

  // Hide the panel entirely until the customer has actually responded.
  const customerResponded = approved.length || declined.length || needsAdvice.length || rebook.length;
  if (!customerResponded) return null;

  const lineTotal = (it) => Number(it.quantity || 1) * Number(it.unit_price || 0);
  const approvedTotal = approved.reduce((s, it) => s + lineTotal(it), 0);
  const declinedTotal = declined.reduce((s, it) => s + lineTotal(it), 0);
  const rebookTotal = rebook.reduce((s, it) => s + lineTotal(it), 0);

  const respondedAt = job.responded_at ? new Date(job.responded_at) : null;
  const dmsUpdated = !!job.dms_updated_at;

  const copyForDms = () => {
    const text = buildDmsText(job, { approved, declined, needsAdvice, rebook, pending });
    navigator.clipboard.writeText(text).then(
      () => toast.success("DMS summary copied — paste into your system"),
      () => toast.error("Couldn't copy"),
    );
  };

  return (
    <div className="card-default p-5 border-2 border-success/40 bg-success/5" data-testid="customer-response-summary">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-overline text-success flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Customer responded
          </div>
          <div className="font-display font-bold text-xl mt-1">Authorisation summary</div>
          {respondedAt && (
            <div className="text-xs text-muted-foreground mt-1">
              {respondedAt.toLocaleString()}
              {job.customer_comment && <span className="ml-2 italic">{`· “${job.customer_comment.slice(0, 60)}${job.customer_comment.length > 60 ? "…" : ""}”`}</span>}
            </div>
          )}
        </div>
        {dmsUpdated ? (
          <span
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-success/15 text-success inline-flex items-center gap-1 whitespace-nowrap"
            title={`DMS updated ${new Date(job.dms_updated_at).toLocaleString()}`}
            data-testid="dms-updated-chip"
          >
            <Check className="w-3 h-3" /> DMS updated
          </span>
        ) : (
          <DmsActionButtons jobId={job.id} onUpdated={onUpdated} />
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-xs">
        <SummaryStat label="Authorised" count={approved.length} value={approvedTotal} variant="success" testid="summary-authorised-stat" />
        <SummaryStat label="Rebook" count={rebook.length} value={rebookTotal} variant="primary" testid="summary-rebook-stat" />
        <SummaryStat label="Declined" count={declined.length} value={declinedTotal} variant="danger" testid="summary-declined-stat" />
        <SummaryStat label="Wants advice" count={needsAdvice.length} value={null} variant="warning" testid="summary-advice-stat" />
      </div>

      <div className="mt-4 space-y-3">
        {approved.length > 0 && (
          <Bucket
            icon={<Check className="w-4 h-4" />}
            label="AUTHORISED — proceed with this work"
            tone="success"
            items={approved}
            lineTotal={lineTotal}
            testid="summary-authorised-list"
          />
        )}
        {rebook.length > 0 && (
          <Bucket
            icon={<Calendar className="w-4 h-4" />}
            label="REBOOK — call customer to arrange return visit"
            tone="primary"
            items={rebook}
            lineTotal={lineTotal}
            testid="summary-rebook-list"
          />
        )}
        {declined.length > 0 && (
          <Bucket
            icon={<X className="w-4 h-4" />}
            label="DECLINED — do not proceed"
            tone="danger"
            items={declined}
            lineTotal={lineTotal}
            testid="summary-declined-list"
          />
        )}
        {needsAdvice.length > 0 && (
          <Bucket
            icon={<MessageSquare className="w-4 h-4" />}
            label="NEEDS ADVICE — call customer back"
            tone="warning"
            items={needsAdvice}
            lineTotal={lineTotal}
            testid="summary-advice-list"
          />
        )}
        {pending.length > 0 && (
          <Bucket
            icon={<Clock className="w-4 h-4" />}
            label="Awaiting response"
            tone="muted"
            items={pending}
            lineTotal={lineTotal}
            testid="summary-pending-list"
          />
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2 items-center justify-between">
        <p className="text-xs text-muted-foreground max-w-md">
          Use the copy button to paste a tidy summary into your DMS, then mark this job as updated so your team knows it&apos;s actioned.
        </p>
        <button onClick={copyForDms} className="btn-outline" data-testid="copy-dms-summary-btn">
          <Copy className="w-4 h-4" /> Copy DMS summary
        </button>
      </div>
    </div>
  );
}


function DmsActionButtons({ jobId, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const mark = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/dms-updated`);
      toast.success("Marked as updated in DMS");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={mark}
      disabled={busy}
      className="btn-primary whitespace-nowrap"
      data-testid="mark-dms-updated-btn"
      title="Mark this job as actioned in your DMS"
    >
      {busy ? <Clock className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
      Mark updated in DMS
    </button>
  );
}


function SummaryStat({ label, count, value, variant, testid }) {
  const tones = {
    success: "border-success/30 bg-success/10 text-success",
    danger: "border-danger/30 bg-danger/10 text-danger",
    warning: "border-warning/30 bg-warning/10 text-warning",
    primary: "border-primary/30 bg-primary/10 text-primary",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[variant]}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className="font-display font-bold text-2xl leading-none">{count}</span>
        {value != null && <span className="font-mono-plex text-xs opacity-80">£{value.toFixed(2)}</span>}
      </div>
    </div>
  );
}


function Bucket({ icon, label, tone, items, lineTotal, testid }) {
  const tones = {
    success: "border-success/30 text-success",
    danger: "border-danger/30 text-danger",
    warning: "border-warning/30 text-warning",
    primary: "border-primary/30 text-primary",
    muted: "border-border text-muted-foreground",
  };
  const total = items.reduce((s, it) => s + lineTotal(it), 0);
  return (
    <div className={`rounded-lg border-l-2 pl-3 py-1 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider">
          {icon} {label}
        </div>
        <span className="font-mono-plex text-xs opacity-80">£{total.toFixed(2)}</span>
      </div>
      <ul className="mt-1 space-y-1">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-foreground truncate">
              {it.name}
              {it.urgency === "red" && <AlertCircle className="inline w-3 h-3 ml-1 text-danger" aria-label="Red flag" />}
            </span>
            <span className="font-mono-plex text-xs text-muted-foreground whitespace-nowrap">
              {it.quantity || 1} × £{Number(it.unit_price || 0).toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function buildDmsText(job, buckets) {
  const lines = [];
  lines.push(`${job.customer_name || "Customer"} — ${job.vehicle_reg || ""}`);
  if (job.responded_at) lines.push(`Customer responded: ${new Date(job.responded_at).toLocaleString()}`);
  lines.push("");
  if (buckets.approved.length) {
    lines.push("AUTHORISED:");
    buckets.approved.forEach((it) => {
      lines.push(`  ✓ ${it.name}  ${it.quantity || 1} × £${Number(it.unit_price || 0).toFixed(2)} = £${(Number(it.quantity || 1) * Number(it.unit_price || 0)).toFixed(2)}`);
    });
    lines.push("");
  }
  if (buckets.rebook && buckets.rebook.length) {
    lines.push("REBOOK — CALL CUSTOMER TO ARRANGE RETURN VISIT:");
    buckets.rebook.forEach((it) => {
      lines.push(`  \u25b6 ${it.name}  ${it.quantity || 1} \u00d7 £${Number(it.unit_price || 0).toFixed(2)}`);
    });
    lines.push("");
  }
  if (buckets.declined.length) {
    lines.push("DECLINED:");
    buckets.declined.forEach((it) => {
      lines.push(`  ✗ ${it.name}  ${it.quantity || 1} × £${Number(it.unit_price || 0).toFixed(2)}`);
    });
    lines.push("");
  }
  if (buckets.needsAdvice.length) {
    lines.push("NEEDS ADVICE — CALLBACK:");
    buckets.needsAdvice.forEach((it) => {
      lines.push(`  ? ${it.name}  ${it.quantity || 1} × £${Number(it.unit_price || 0).toFixed(2)}`);
    });
    lines.push("");
  }
  if (job.customer_comment) {
    lines.push("CUSTOMER COMMENT:");
    lines.push(`  "${job.customer_comment}"`);
  }
  return lines.join("\n").trim();
}
